/* eslint-disable react/prop-types */
import BlockContentToReact from '@sanity/block-content-to-react';
import {
    Heading,
    Text,
    List,
    ListItem,
    OrderedList,
    UnorderedList,
    Code,
    Link,
} from '@chakra-ui/react';
import useLocale from '../lib/useLocale';
import { SanityLocaleBlockContent } from '../../graphql-types';

interface BlockContentProps {
    data: SanityLocaleBlockContent;
}

const BlockRenderer = props => {
    const {
        node: { style = 'normal' },
        children,
    } = props;

    switch (style) {
        case 'h1':
            return (
                <Heading as="h1" size="2xl" my="8">
                    {children}
                </Heading>
            );
        case 'h2':
            return (
                <Heading as="h2" size="xl" my="6">
                    {children}
                </Heading>
            );
        case 'h3':
            return (
                <Heading as="h3" size="lg" my="5">
                    {children}
                </Heading>
            );
        case 'h4':
            return (
                <Heading as="h4" size="md" my="4">
                    {children}
                </Heading>
            );
        case 'blockquote':
            return (
                <Code as="blockquote" my="2" p={2} bg="primary.100">
                    {children}
                </Code>
            );
        case 'normal':
            return (
                <Text my="2" lineHeight="tall">
                    {children}
                </Text>
            );
        default:
            return BlockContentToReact.defaultSerializers.types.block(props);
    }
};

const CustomListItem = ({ children }) => <ListItem>{children}</ListItem>;

const CustomList = ({ children, type }) => {
    if (type === 'bullet')
        return <UnorderedList spacing={3}>{children}</UnorderedList>;
    if (type === 'number')
        return <OrderedList spacing={3}>{children}</OrderedList>;
    return <List spacing={3}>{children}</List>;
};

const CustomLink = ({ mark, children }) => (
    <Link href={mark.href}>{children}</Link>
);

export default function BlockContent({ data }: BlockContentProps) {
    const { translateBlock } = useLocale();
    return (
        <BlockContentToReact
            blocks={translateBlock(data)}
            serializers={{
                types: { block: BlockRenderer },
                listItem: CustomListItem,
                list: CustomList,
                marks: {
                    link: CustomLink,
                },
            }}
            projectId={process.env.GATSBY_SANITY_PROJECT_ID}
            dataset={process.env.GATSBY_SANITY_DATASET}
        />
    );
}
